import { useState } from "react";
import { AlertCircle, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import * as api from "@/services/api";

interface ValidateAllDialogProps {
	trigger?: React.ReactNode;
}

export function ValidateAllDialog({ trigger }: ValidateAllDialogProps) {
	const [open, setOpen] = useState(false);

	const { data, isLoading, refetch, isRefetching } = useQuery({
		queryKey: ["validate-dependencies"],
		queryFn: api.validateAllDependencies,
		enabled: open,
		staleTime: 0,
	});

	const handleOpenChange = (newOpen: boolean) => {
		setOpen(newOpen);
		if (newOpen) {
			refetch();
		}
	};

	const totalIssues = (data?.errors?.length || 0) + (data?.warnings?.length || 0);

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogTrigger asChild>
				{trigger || (
					<Button variant="outline" size="sm">
						<CheckCircle2 className="h-4 w-4 mr-2" />
						Validate All
					</Button>
				)}
			</DialogTrigger>
			<DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						Dependency Validation Results
						{isLoading || isRefetching ? (
							<Loader2 className="h-4 w-4 animate-spin" />
						) : data?.valid ? (
							<Badge variant="default" className="bg-green-500">
								Valid
							</Badge>
						) : (
							<Badge variant="destructive">
								{totalIssues} Issue{totalIssues !== 1 ? "s" : ""}
							</Badge>
						)}
					</DialogTitle>
					<DialogDescription>
						Check all feature dependencies for circular references and missing
						features.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 mt-4">
					{isLoading ? (
						<div className="flex items-center justify-center py-8">
							<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
							<span className="ml-2 text-muted-foreground">
								Validating dependencies...
							</span>
						</div>
					) : data?.valid ? (
						<div className="flex items-center gap-3 p-4 rounded-lg bg-green-50 border border-green-200">
							<CheckCircle2 className="h-5 w-5 text-green-600" />
							<div>
								<p className="font-medium text-green-800">
									All dependencies are valid
								</p>
								<p className="text-sm text-green-700">
									No circular references or missing features detected.
								</p>
							</div>
						</div>
					) : (
						<>
							{/* Errors */}
							{data?.errors && data.errors.length > 0 && (
								<div className="space-y-2">
									<h4 className="text-sm font-medium flex items-center gap-2 text-red-700">
										<AlertCircle className="h-4 w-4" />
										Errors ({data.errors.length})
									</h4>
									<div className="space-y-2">
										{data.errors.map((error, index) => (
											<div
												key={index}
												className="p-3 rounded-md bg-red-50 border border-red-200"
											>
												<div className="flex items-start gap-2">
													<Badge
														variant="destructive"
														className="text-xs flex-shrink-0"
													>
														{error.type === "circular"
															? "Circular"
															: error.type === "missing_feature"
																? "Missing"
																: "Invalid"}
													</Badge>
													<div className="flex-1">
														<p className="text-sm font-medium text-red-800 font-mono">
															{error.feature}
														</p>
														<p className="text-sm text-red-700">
															{error.message}
														</p>
														{error.chain && error.chain.length > 0 && (
															<p className="text-xs text-red-600 mt-1 font-mono">
																{error.chain.join(" → ")}
															</p>
														)}
													</div>
												</div>
											</div>
										))}
									</div>
								</div>
							)}

							{/* Warnings */}
							{data?.warnings && data.warnings.length > 0 && (
								<div className="space-y-2">
									<h4 className="text-sm font-medium flex items-center gap-2 text-yellow-700">
										<AlertTriangle className="h-4 w-4" />
										Warnings ({data.warnings.length})
									</h4>
									<div className="space-y-2">
										{data.warnings.map((warning, index) => (
											<div
												key={index}
												className="p-3 rounded-md bg-yellow-50 border border-yellow-200"
											>
												<div className="flex items-start gap-2">
													<Badge
														variant="outline"
														className="text-xs flex-shrink-0 border-yellow-400 text-yellow-700"
													>
														{warning.type === "unused"
															? "Unused"
															: "Orphan"}
													</Badge>
													<div className="flex-1">
														<p className="text-sm font-medium text-yellow-800 font-mono">
															{warning.feature}
														</p>
														<p className="text-sm text-yellow-700">
															{warning.message}
														</p>
													</div>
												</div>
											</div>
										))}
									</div>
								</div>
							)}
						</>
					)}

					{/* Refresh Button */}
					<div className="flex justify-end pt-2 border-t">
						<Button
							variant="outline"
							size="sm"
							onClick={() => refetch()}
							disabled={isRefetching}
						>
							{isRefetching ? (
								<>
									<Loader2 className="h-4 w-4 mr-2 animate-spin" />
									Refreshing...
								</>
							) : (
								"Refresh"
							)}
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
}
